import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { CompareProvider } from './context/CompareContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { StickerProvider } from './context/StickerContext';
import { LoanProvider } from './context/LoanContext';
import { Toaster } from 'react-hot-toast';
import API_BASE_URL from './config';
import ScrollToTop from './components/ScrollToTop';
import Header from "./components/Header";
import Footer from "./components/Footer";
import CompareFloatingButton from './components/CompareFloatingButton';
import ChatWidget from './components/ChatWidget';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { useLocation } from 'react-router-dom';
import PublicProducts from './pages/PublicProducts';
import AdminUsers from './pages/AdminUsers';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import Admin, { AdminHome } from './pages/Admin';
import AdminSettings from './pages/AdminSettings';
import AdminCampaigns from './pages/AdminCampaigns';
import AdminOrders from './pages/AdminOrders';
import AdminReports from './pages/AdminReports';
import AdminProducts from './pages/AdminProducts';
import AdminPages from './pages/AdminPages';
import AdminMedia from './pages/AdminMedia';
import AdminChat from './pages/AdminChat';
import ServicesAdmin from './pages/ServicesAdmin';
import AdminShopLocations from './pages/AdminShopLocations';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import ProductView from './pages/ProductView';
import Category from './pages/Category';
import CartPage from './pages/Cart';
import Checkout from './pages/Checkout';
import Wishlist from './pages/Wishlist';
import BlogsPage from './pages/Blogs';
import BlogPost from './pages/BlogPost';
import BlogsAdmin from './pages/BlogsAdmin';
import CategoriesAdmin from './pages/CategoriesAdmin';
import OffersPage from './pages/Offers';
import Sherbimet from './pages/Sherbimet';
import Financim from './pages/Financim';
import StaticPage from './pages/StaticPage';
import ComparePage from './pages/ComparePage';
import NotFound from './pages/NotFound';
import OrderResult from './pages/OrderResult';

function LayoutWrapper({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      {!isAdmin && <CompareFloatingButton />}
      {!isAdmin && <ChatWidget />}
      {children}
      {!isAdmin && <Footer />}
    </>
  );
}

function App() {
  useEffect(() => {
    // Fetch basic settings to apply dynamic siteFavicon
    fetch(`${API_BASE_URL}/settings`)
      .then(res => res.json())
      .then(data => {
        if (data && data.siteTitle) {
          document.title = data.siteTitle;
        }
        if (data && data.siteFavicon) {
          const faviconUrl = data.siteFavicon.startsWith('http')
            ? data.siteFavicon
            : `${API_BASE_URL.replace('/api', '')}${data.siteFavicon}`;

          let iconLink = document.querySelector("link[rel~='icon']");
          if (!iconLink) {
            iconLink = document.createElement('link');
            iconLink.rel = 'icon';
            document.head.appendChild(iconLink);
          }
          iconLink.href = faviconUrl;

          let appleIconLink = document.querySelector("link[rel='apple-touch-icon']");
          if (!appleIconLink) {
            appleIconLink = document.createElement('link');
            appleIconLink.rel = 'apple-touch-icon';
            document.head.appendChild(appleIconLink);
          }
          appleIconLink.href = faviconUrl;
        }

        if (data && data.headerTags) {
          try {
            const range = document.createRange();
            range.selectNode(document.head);
            const documentFragment = range.createContextualFragment(data.headerTags);
            document.head.appendChild(documentFragment);
          } catch (err) {
            console.error('Failed to inject global header tags:', err);
          }
        }

        if (data && data.bodyTags) {
          try {
            const range = document.createRange();
            range.selectNode(document.body);
            const documentFragment = range.createContextualFragment(data.bodyTags);
            document.body.insertBefore(documentFragment, document.body.firstChild);
          } catch (err) {
            console.error('Failed to inject global body tags:', err);
          }
        }
      })
      .catch(e => console.error('Failed to load global settings', e));
  }, []);

  // Global scroll-driven reveal effect for sections, cards & modules
  useEffect(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
    );

    const observeElements = () => {
      const targets = document.querySelectorAll(
        'section:not(.reveal-item), .product-card:not(.reveal-item), .products-card:not(.reveal-item), .mac-blog-card:not(.reveal-item), .service-card:not(.reveal-item), .feature-box:not(.reveal-item), .pr-offer-card:not(.reveal-item), .module-box:not(.reveal-item), .cards-wrapper > div:not(.reveal-item)'
      );
      targets.forEach((el) => {
        // Skip header/nav from hiding
        if (el.closest('header') || el.closest('.site-header-bar')) return;
        el.classList.add('reveal-item');
        observer.observe(el);
      });
    };

    observeElements();
    const interval = setInterval(observeElements, 400);
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <CurrencyProvider>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 999999 }}
      />
      <CompareProvider>
        <StickerProvider>
          <LoanProvider>
            <Router>
              <LayoutWrapper>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/admin" element={<Admin />}>
                    <Route index element={<AdminHome />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="reports" element={<AdminReports />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="services" element={<ServicesAdmin />} />
                    <Route path="pages" element={<AdminPages />} />
                    <Route path="media" element={<AdminMedia />} />
                    <Route path="chat" element={<AdminChat />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="blogs" element={<BlogsAdmin />} />
                    <Route path="categories" element={<CategoriesAdmin />} />
                    <Route path="settings" element={<AdminSettings />} />
                    <Route path="fushatat" element={<AdminCampaigns />} />
                    <Route path="shops" element={<AdminShopLocations />} />
                  </Route>
                  <Route path="/register" element={<Register />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/auth/success" element={<AuthCallback />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/users/:id" element={<UserProfile />} />
                  <Route path="/products" element={<PublicProducts />} />
                  <Route path="/products/:id" element={<ProductView />} />
                  <Route path="/category" element={<Category />} />
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/offers" element={<OffersPage />} />
                  <Route path="/sherbimet" element={<Sherbimet />} />
                  <Route path="/financim" element={<Financim />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-result" element={<OrderResult />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/blog" element={<BlogsPage />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/blogs" element={<BlogsPage />} />
                  <Route path="/blogs/:id" element={<BlogPost />} />
                  <Route path="/:slug" element={<StaticPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </LayoutWrapper>
            </Router>
          </LoanProvider>
        </StickerProvider>
      </CompareProvider>
    </CurrencyProvider>
  );
}



export default App;
